import React, { useState, useEffect, useContext } from 'react'
import Link from 'next/link'
import { useElements, useStripe, CardNumberElement } from '@stripe/react-stripe-js'
import { useForm } from 'react-hook-form'
import { useMutation } from '@apollo/client'
import TextInput from 'components/generic/Input/TextInput'
import {
  CardCvcInput,
  CardNumberInput,
  CardExpiryInput,
  FormRow,
  PaymentTerms,
  PaymentForm,
} from './StripePaymentForm.styles'
import InputErrorMessage from 'components/generic/Input/InputErrorMessage'
import {
  StripeCardFormErrors,
  isValidCardholderName,
  purchaseAndReturnRedirectUrl,
  getPaymentReturnUrl,
} from 'utils/stripeUtils'

import PURCHASE_SUBSCRIPTION from 'components/api/mutations/purchaseSubscription.gql'
import PURCHASE_SOURCE from 'components/api/mutations/purchaseSource.gql'

import { stripeInputStyles } from 'components/generic/Input/styles'
import { useClientHost } from 'hooks/useClientHost'
import { GET_STARTED_ROUTES } from 'components/GetStarted/constants'
import FormSubmissionError from 'components/generic/FormSubmissionError'
import LoadingIndicatorButton from 'components/generic/LoadingIndicatorButton'
import { AppContext } from 'contexts/AppContext'
import { ApplePay } from 'components/ApplePay'
import { trackEvent } from 'utils/Analytics'
import { useStripeCardFields } from './useStripeCardFields'

const cardholderNameValidationRules = {
  required: 'Please enter cardholder name.',
  validate: {
    isValid: (value) => isValidCardholderName(value) || 'Cardholder name is invalid.',
  },
}

export interface StripePaymentForm {
  countryCode: string
  cardholderName: string
  currency: string
  stripePlan: any
  relativePaymentReturnUrl: string
  onPurchaseComplete(redirectUrl?: string): void
  submissionError?: string
  conversionType: string
  flow: string
}

const StripePaymentForm = ({
  countryCode,
  cardholderName,
  currency,
  stripePlan,
  relativePaymentReturnUrl,
  onPurchaseComplete,
  submissionError,
  conversionType,
  flow,
}: StripePaymentForm) => {
  const { domain } = useContext(AppContext)
  const host = useClientHost()
  const [purchaseSubscription] = useMutation(PURCHASE_SUBSCRIPTION)
  const [purchaseSource] = useMutation(PURCHASE_SOURCE)

  const { register, errors: hookFormErrors, handleSubmit, formState, trigger } = useForm({
    mode: 'all',
    defaultValues: {
      name: cardholderName,
    },
  })
  const elements = useElements()
  const stripe = useStripe()

  const [submission, setSubmission] = useState({ error: null, isSubmitting: false })
  const [applePayError, setApplePayError] = useState('')

  const {
    errors: stripeFormErrors,
    isValid: isStripeFieldDataValid,
    onCardNumberChange,
    onCardExpiryChange,
    onCvcChange,
  } = useStripeCardFields()

  const errors: StripeCardFormErrors = {
    ...hookFormErrors,
    ...stripeFormErrors,
  }

  const onSubmit = async (formData) => {
    try {
      setSubmission({ error: null, isSubmitting: true })
      const paymentReturnUrl = getPaymentReturnUrl({
        host,
        stripePlan,
        baseUrl: relativePaymentReturnUrl,
        fullName: formData.name,
      })
      const redirectUrl = await purchaseAndReturnRedirectUrl({
        stripe,
        stripePlan,
        purchaseSource,
        purchaseSubscription,
        redirectUrl: paymentReturnUrl,
        cardNumberElement: elements.getElement(CardNumberElement),
        cardholderName: formData.name,
        postalCode: formData.postal,
        flow,
        conversionType,
      })
      trackEvent(domain, 'Subscription Created', {
        stripePlan: stripePlan.stripe_plan_id,
        paymentMethod: 'Credit Card',
      })
      onPurchaseComplete(redirectUrl)
    } catch (err) {
      const isGraphQLError = Array.isArray(err?.graphQLErrors) && err.graphQLErrors.length > 0
      const error = isGraphQLError ? err.graphQLErrors[0].message : err.message
      setSubmission({ error, isSubmitting: false })
    }
  }

  useEffect(() => {
    if (cardholderName) {
      trigger('name')
    }
  }, [cardholderName, trigger])

  useEffect(() => {
    if (submissionError) {
      setSubmission({ error: true, isSubmitting: false })
    }
  }, [submissionError])

  return (
    <PaymentForm onSubmit={handleSubmit(onSubmit)}>
      <FormSubmissionError
        isVisible={submission.error}
        heading='Your transaction could not be processed.'
      >
        Please check that your card details are entered correctly and contact your bank for
        assistance. Or,{' '}
        <Link href='/get-started/[step]' as={`/get-started/${GET_STARTED_ROUTES.PLANS}`}>
          <a>go back</a>
        </Link>
        .
      </FormSubmissionError>
      <FormSubmissionError
        isVisible={!!applePayError}
        heading='Your payment couldn’t be completed.'
      >
        Please enter your credit card details below.
      </FormSubmissionError>
      <ApplePay
        flow={flow}
        conversionType={conversionType}
        countryCode={countryCode}
        currency={currency}
        stripePlan={stripePlan}
        errorText={applePayError}
        setErrorText={(newApplePayError) => {
          setApplePayError(newApplePayError)
          setSubmission({ error: false, isSubmitting: false })
        }}
        clearErrorText={() => {
          setApplePayError('')
        }}
        disableSubmit={submission.isSubmitting}
        setDisableSubmit={(disableSubmit) => {
          setSubmission({ error: submission.error, isSubmitting: disableSubmit })
        }}
        onPurchaseComplete={onPurchaseComplete}
      />
      <TextInput
        id='payment-name'
        name='name'
        autoFocus={true}
        autoComplete='cc-name'
        disabled={submission.isSubmitting}
        label='Cardholder Name'
        isValid={!errors.name}
        ref={register(cardholderNameValidationRules)}
      />
      <InputErrorMessage errors={errors} inputId='payment-name' name='name' />
      <TextInput label='Credit or Debit Card'>
        <CardNumberInput
          id='stripe-card-number'
          onChange={onCardNumberChange}
          isValid={!errors.cardnumber}
          options={{
            style: stripeInputStyles,
            disabled: submission.isSubmitting,
          }}
        />
      </TextInput>
      <InputErrorMessage errors={errors} inputId='stripe-card-number' name='cardnumber' />
      <FormRow>
        <div>
          <TextInput label='Expiry'>
            <CardExpiryInput
              id='stripe-card-expiry'
              onChange={onCardExpiryChange}
              isValid={!errors['exp-date']}
              options={{
                style: stripeInputStyles,
                disabled: submission.isSubmitting,
              }}
            />
          </TextInput>
          <InputErrorMessage errors={errors} inputId='stripe-card-expiry' name='exp-date' />
        </div>

        <div>
          <TextInput label='CVC'>
            <CardCvcInput
              id='stripe-card-cvc'
              disabled={true}
              onChange={onCvcChange}
              isValid={!errors['cvc']}
              options={{
                style: stripeInputStyles,
                disabled: submission.isSubmitting,
              }}
            />
          </TextInput>
          <InputErrorMessage errors={errors} inputId='stripe-card-cvc' name='cvc' />
        </div>
      </FormRow>
      <TextInput
        id='payment-postal-code'
        name='postal'
        disabled={submission.isSubmitting}
        autoComplete='shipping postal-code'
        placeholder='90210'
        label='Postal Code'
        isValid={!errors.postal}
        ref={register({ required: 'Please enter postal code.' })}
        width='45%'
      />
      <InputErrorMessage errors={errors} inputId='payment-postal-code' name='postal' />
      <PaymentTerms>
        This subscription will renew automatically every {stripePlan?.interval_in_months_label} from
        the date of purchase.
        <br />
        <a
          target='_empty'
          href='https://help.careguide.com/hc/en-us/articles/206330765-How-do-I-cancel-my-subscription-'
        >
          Cancel any time
        </a>{' '}
        or contact us at <a href='info@careguide.com'>info@careguide.com</a> / 1-855-874-8837.
      </PaymentTerms>

      <LoadingIndicatorButton
        mt='1rem'
        width='100%'
        disabled={!isStripeFieldDataValid || !formState.isValid}
        isLoading={submission.isSubmitting}
      >
        {submission.isSubmitting ? 'Processing Payment' : 'Submit'}
      </LoadingIndicatorButton>
    </PaymentForm>
  )
}

export default StripePaymentForm
